import { useReveal } from '@/lib/useReveal';

export function FadeUp({ children, delay = 0, className = '', as: Comp = 'div', ...rest }) {
  const [ref, shown] = useReveal();
  return (
    <Comp
      ref={ref}
      style={{
        transform: shown ? 'translateY(0)' : 'translateY(28px)',
        opacity: shown ? 1 : 0,
        transition: `opacity 0.8s cubic-bezier(.25,1,.5,1) ${delay}s, transform 0.9s cubic-bezier(.25,1,.5,1) ${delay}s`,
      }}
      className={className}
      {...rest}
    >
      {children}
    </Comp>
  );
}

export function ScaleIn({ children, delay = 0, className = '' }) {
  const [ref, shown] = useReveal();
  return (
    <div
      ref={ref}
      style={{
        transform: shown ? 'scale(1)' : 'scale(0.96)',
        opacity: shown ? 1 : 0,
        transition: `opacity 0.7s ease ${delay}s, transform 0.9s cubic-bezier(.25,1,.5,1) ${delay}s`,
      }}
      className={className}
    >
      {children}
    </div>
  );
}

export function BlurReveal({ children, delay = 0, className = '' }) {
  const [ref, shown] = useReveal();
  return (
    <div
      ref={ref}
      style={{
        filter: shown ? 'blur(0px)' : 'blur(14px)',
        opacity: shown ? 1 : 0,
        transition: `opacity 0.8s ease ${delay}s, filter 0.9s ease ${delay}s`,
      }}
      className={className}
    >
      {children}
    </div>
  );
}

export function TextReveal({ text, className = '', delay = 0 }) {
  const [ref, shown] = useReveal();
  const words = text.split(' ');
  return (
    <span ref={ref} className={`${className} whitespace-normal`}>
      {words.map((w, i) => (
        <span key={i} className="reveal-mask align-baseline">
          <span
            style={{
              // display: 'inline-block',
              transform: shown ? 'translateY(0)' : 'translateY(110%)',
              transition: `transform 0.55s cubic-bezier(.65,0,.35,1) ${delay + i * 0.04}s`,
            }}
          >
            {/* A plain trailing space here gets collapsed to zero width by the browser,
                because each word sits in its own `overflow:hidden; display:inline-block`
                mask span (needed for the per-word reveal animation) — CSS treats the space
                as trailing whitespace at the edge of that box and drops it, which made
                every multi-word TextReveal headline on the site run together with no gaps
                (e.g. "WebVitalsin2026"). A non-breaking space isn't collapsible, so it
                always renders with real width. Skip it after the last word. */}
            {w}{i < words.length - 1 ? ' ' : ''}
          </span>
        </span>
      ))}
    </span>
  );
}

export function Counter({ from = 0, to, suffix = '', duration = 1800, className = '' }) {
  const [ref, shown] = useReveal();
  const display = useCounter(from, to, duration, shown);
  return <span ref={ref} className={className}>{display}{suffix}</span>;
}

import { useEffect, useState } from 'react';
function useCounter(from, to, dur, active) {
  const [v, setV] = useState(from);
  useEffect(() => {
    if (!active) return;
    const start = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setV(Math.round(from + (to - from) * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, from, to, dur]);
  return v;
}
